import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { MasterDialog } from "./MasterDialog";
import {
  GST_STATUS_LABELS, MSME_LABELS, ROLE_LABELS, isValidGstinChecksum, partnerHasErrors, msmeDueStatus,
  normalisePartner, stateFromGstin, validatePartner,
  type Partner, type PartnerAddress, type PartnerGstStatus, type PartnerMsme, type PartnerRole
} from "./partner";
import type { Party, PartyType } from "./types";

// ------------------------------------------------------------- adapters

/** Party is the storage shape; Partner is the richer validation shape. This
 *  pair keeps the two in sync without migrating storage. */
const partyToPartner = (party: Partial<Party>): Partial<Partner> => ({
  id: party.id,
  code: party.id ? `PTR-${party.id.slice(-5)}` : undefined,
  name: party.name,
  legalName: party.legalName,
  kind: "business",
  roles: party.type === "Customer" ? ["customer"] : party.type === "Vendor" ? ["vendor"] : party.type === "Employee" ? ["employee"] : ["customer"],
  gstin: party.gstin,
  gstStatus: party.gstStatus ?? (party.gstin ? "regular" : "unregistered"),
  pan: party.pan,
  tan: party.tan,
  msme: party.msme,
  billingAddress: party.billingAddress ?? { state: party.state },
  shippingAddress: party.shippingAddress,
  bank: party.bank,
  creditLimit: party.creditLimit,
  creditDays: party.creditDays,
  defaultTdsSection: party.defaultTdsSection,
  rcmApplicable: party.rcmApplicable,
  active: party.active ?? true
});

const partnerToParty = (partner: Partner): Party => ({
  id: partner.id,
  name: partner.name,
  type: partner.roles.includes("customer") && partner.roles.includes("vendor")
    ? "Customer" // dual-role parties still need a primary type for the old list view
    : partner.roles.includes("vendor") ? "Vendor" : partner.roles.includes("employee") ? "Employee" : "Customer",
  gstin: partner.gstin,
  pan: partner.pan,
  state: partner.billingAddress?.state ?? "Karnataka",
  openBalance: 0,
  risk: "Low",
  active: partner.active,
  legalName: partner.legalName,
  tan: partner.tan,
  gstStatus: partner.gstStatus,
  msme: partner.msme,
  billingAddress: partner.billingAddress,
  shippingAddress: partner.shippingAddress,
  bank: partner.bank,
  creditLimit: partner.creditLimit,
  creditDays: partner.creditDays,
  defaultTdsSection: partner.defaultTdsSection,
  rcmApplicable: partner.rcmApplicable
});

// -------------------------------------------------------------- widgets

function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="pf-field">
      <span className="pf-label">{label}</span>
      {children}
      {error ? <small className="pf-error">{error}</small> : hint ? <small className="pf-hint">{hint}</small> : null}
    </label>
  );
}

const ROLE_OPTIONS: PartnerRole[] = ["customer", "vendor", "related-party", "employee"];
const GST_STATUS_OPTIONS: PartnerGstStatus[] = ["regular", "unregistered", "composition", "sez", "overseas", "uin", "deemed-export"];
const MSME_OPTIONS: PartnerMsme["category"][] = ["not-registered", "micro", "small", "medium", "not-applicable"];

const emptyAddress: PartnerAddress = {};

// ------------------------------------------------------------- the form

export interface PartnerFormProps {
  initial?: Partial<Party>;
  /** Pre-select a role — used when quick-created from an invoice/bill line. */
  defaultRole?: PartnerRole;
  onSave: (party: Party) => void | Promise<unknown>;
  onCancel: () => void;
}

export function PartnerForm({ initial, defaultRole, onSave, onCancel }: PartnerFormProps) {
  const seed = partyToPartner(initial ?? {});
  const [saving,setSaving]=useState(false),[saveError,setSaveError]=useState('');
  const savingRef=useRef(false);

  const [name, setName] = useState(seed.name ?? "");
  const [legalName, setLegalName] = useState(seed.legalName ?? "");
  const [roles, setRoles] = useState<PartnerRole[]>(seed.roles?.length ? seed.roles : defaultRole ? [defaultRole] : ["customer"]);
  const [gstin, setGstin] = useState(seed.gstin ?? "");
  const [gstStatus, setGstStatus] = useState<PartnerGstStatus>(seed.gstStatus ?? "unregistered");
  const [pan, setPan] = useState(seed.pan ?? "");
  const [tan, setTan] = useState(seed.tan ?? "");
  const [billing, setBilling] = useState<PartnerAddress>(seed.billingAddress ?? emptyAddress);
  const [shipSameAsBilling, setShipSameAsBilling] = useState(!seed.shippingAddress);
  const [shipping, setShipping] = useState<PartnerAddress>(seed.shippingAddress ?? emptyAddress);
  const [msmeCategory, setMsmeCategory] = useState<PartnerMsme["category"]>(seed.msme?.category ?? "not-registered");
  const [udyamNumber, setUdyamNumber] = useState(seed.msme?.udyamNumber ?? "");
  const [agreedCreditDays, setAgreedCreditDays] = useState<number | "">(seed.msme?.agreedCreditDays ?? "");
  const [bankAccountName, setBankAccountName] = useState(seed.bank?.accountName ?? "");
  const [bankAccountNumber, setBankAccountNumber] = useState(seed.bank?.accountNumber ?? "");
  const [bankIfsc, setBankIfsc] = useState(seed.bank?.ifsc ?? "");
  const [bankName, setBankName] = useState(seed.bank?.bankName ?? "");
  const [creditLimit, setCreditLimit] = useState<number | "">(seed.creditLimit ?? "");
  const [creditDays, setCreditDays] = useState<number | "">(seed.creditDays ?? "");

  const draft: Partial<Partner> = {
    id: seed.id ?? "new",
    name,
    legalName: legalName || undefined,
    roles,
    gstin: gstin || undefined,
    gstStatus,
    pan: pan || undefined,
    tan: tan || undefined,
    billingAddress: billing,
    msme: msmeCategory === "not-registered" ? { category: msmeCategory } : {
      category: msmeCategory,
      udyamNumber: udyamNumber || undefined,
      agreedCreditDays: agreedCreditDays === "" ? undefined : Number(agreedCreditDays)
    }
  };

  const issues = useMemo(() => validatePartner(draft), [name, legalName, roles.join(), gstin, gstStatus, pan, tan, msmeCategory, udyamNumber, agreedCreditDays, JSON.stringify(billing)]);
  const errors = issues.filter((issue) => issue.severity === "error");
  const warnings = issues.filter((issue) => issue.severity === "warning");
  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;

  const detectedState = stateFromGstin(gstin);
  const gstinLooksValid = gstin.length === 15 && isValidGstinChecksum(gstin.toUpperCase());

  const msmePreview = msmeCategory === "micro" || msmeCategory === "small"
    ? msmeDueStatus(
        { roles, msme: { category: msmeCategory, agreedCreditDays: agreedCreditDays === "" ? undefined : Number(agreedCreditDays) } },
        new Date().toISOString().slice(0, 10),
        undefined,
        new Date().toISOString().slice(0, 10)
      )
    : undefined;

  const toggleRole = (role: PartnerRole) =>
    setRoles((current) => (current.includes(role) ? current.filter((entry) => entry !== role) : [...current, role]));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (partnerHasErrors(issues) || savingRef.current) return;
    const partner = normalisePartner({
      id: seed.id ?? `party-${Date.now().toString(36)}`,
      code: seed.code ?? `PTR-${Date.now().toString(36).slice(-5).toUpperCase()}`,
      name,
      legalName: legalName || undefined,
      kind: "business",
      roles,
      gstin: gstin || undefined,
      gstStatus,
      pan: pan || undefined,
      tan: tan || undefined,
      billingAddress: billing,
      shippingAddress: shipSameAsBilling ? undefined : shipping,
      msme: {
        category: msmeCategory,
        udyamNumber: udyamNumber || undefined,
        agreedCreditDays: agreedCreditDays === "" ? undefined : Number(agreedCreditDays)
      },
      bank: bankAccountNumber || bankIfsc ? { accountName: bankAccountName || undefined, accountNumber: bankAccountNumber || undefined, ifsc: bankIfsc || undefined, bankName: bankName || undefined } : undefined,
      creditLimit: creditLimit === "" ? undefined : Number(creditLimit),
      creditDays: creditDays === "" ? undefined : Number(creditDays),
      active: true,
      currency: "INR",
      createdAt: seed.createdAt ?? new Date().toISOString()
    });
    savingRef.current=true;setSaving(true);setSaveError('');
    try { await onSave(partnerToParty(partner)); }
    catch(error){setSaveError(error instanceof Error?error.message:'Master could not be saved. Your entries are retained.');}
    finally{savingRef.current=false;setSaving(false);}
  };

  return (
    <form onSubmit={submit} className="pf-form">
      {saveError&&<p role="alert">{saveError}</p>}
      <fieldset disabled={saving} className="editor-fieldset">
      <section className="pf-section">
        <h4>Basic details</h4>
        <div className="pf-grid two">
          <Field label="Display name" error={errorFor("name")}>
            <input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder="How this partner appears in lists" />
          </Field>
          <Field label="Legal name" hint="Only if different from the display name">
            <input value={legalName} onChange={(event) => setLegalName(event.target.value)} placeholder="Full registered name" />
          </Field>
        </div>
        <Field label="Roles" error={errorFor("roles")} hint="A partner can be more than one at once — a business that both buys from and sells to you needs only one master.">
          <div className="pf-chip-row">
            {ROLE_OPTIONS.map((role) => (
              <button key={role} type="button" className={`pf-chip ${roles.includes(role) ? "on" : ""}`} onClick={() => toggleRole(role)}>
                {ROLE_LABELS[role]}
              </button>
            ))}
          </div>
        </Field>
      </section>

      <section className="pf-section">
        <h4>GST &amp; PAN</h4>
        <div className="pf-grid two">
          <Field label="GST registration status">
            <select value={gstStatus} onChange={(event) => setGstStatus(event.target.value as PartnerGstStatus)}>
              {GST_STATUS_OPTIONS.map((status) => <option key={status} value={status}>{GST_STATUS_LABELS[status]}</option>)}
            </select>
          </Field>
          <Field
            label="GSTIN"
            error={errorFor("gstin")}
            hint={gstin && gstinLooksValid ? `Valid — ${detectedState?.name ?? "state not recognised"}` : gstin ? "Checked against the GSTN checksum, not just the format" : undefined}
          >
            <input
              value={gstin}
              onChange={(event) => setGstin(event.target.value.toUpperCase())}
              placeholder="29ABCDE1234F1Z5"
              maxLength={15}
              className={gstin && !gstinLooksValid ? "pf-input-invalid" : gstin && gstinLooksValid ? "pf-input-valid" : ""}
            />
          </Field>
        </div>
        <div className="pf-grid two">
          <Field label="PAN" error={errorFor("pan")}>
            <input value={pan} onChange={(event) => setPan(event.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} />
          </Field>
          <Field label="TAN" error={errorFor("tan")} hint="Only if this partner deducts TDS on payments to you">
            <input value={tan} onChange={(event) => setTan(event.target.value.toUpperCase())} placeholder="BLRA12345B" maxLength={10} />
          </Field>
        </div>
      </section>

      <section className="pf-section">
        <h4>Billing address</h4>
        <div className="pf-grid two">
          <Field label="Address line 1"><input value={billing.line1 ?? ""} onChange={(event) => setBilling({ ...billing, line1: event.target.value })} /></Field>
          <Field label="Address line 2"><input value={billing.line2 ?? ""} onChange={(event) => setBilling({ ...billing, line2: event.target.value })} /></Field>
        </div>
        <div className="pf-grid three">
          <Field label="City"><input value={billing.city ?? ""} onChange={(event) => setBilling({ ...billing, city: event.target.value })} /></Field>
          <Field label="State"><input value={billing.state ?? ""} onChange={(event) => setBilling({ ...billing, state: event.target.value })} placeholder="Karnataka" /></Field>
          <Field label="PIN code"><input value={billing.pincode ?? ""} onChange={(event) => setBilling({ ...billing, pincode: event.target.value })} maxLength={6} /></Field>
        </div>
      </section>

      <section className="pf-section">
        <h4>Shipping address</h4>
        <label className="pf-checkbox">
          <input type="checkbox" checked={shipSameAsBilling} onChange={(event) => setShipSameAsBilling(event.target.checked)} />
          Same as billing address
        </label>
        {!shipSameAsBilling && (
          <>
            <div className="pf-grid two">
              <Field label="Address line 1"><input value={shipping.line1 ?? ""} onChange={(event) => setShipping({ ...shipping, line1: event.target.value })} /></Field>
              <Field label="Address line 2"><input value={shipping.line2 ?? ""} onChange={(event) => setShipping({ ...shipping, line2: event.target.value })} /></Field>
            </div>
            <div className="pf-grid three">
              <Field label="City"><input value={shipping.city ?? ""} onChange={(event) => setShipping({ ...shipping, city: event.target.value })} /></Field>
              <Field label="State"><input value={shipping.state ?? ""} onChange={(event) => setShipping({ ...shipping, state: event.target.value })} /></Field>
              <Field label="PIN code"><input value={shipping.pincode ?? ""} onChange={(event) => setShipping({ ...shipping, pincode: event.target.value })} maxLength={6} /></Field>
            </div>
          </>
        )}
      </section>

      <section className="pf-section">
        <h4>MSME classification</h4>
        <div className="pf-grid two">
          <Field label="Category" hint="Section 15 of the MSMED Act caps the agreed credit period at 45 days for micro and small enterprises.">
            <select value={msmeCategory} onChange={(event) => setMsmeCategory(event.target.value as PartnerMsme["category"])}>
              {MSME_OPTIONS.map((category) => <option key={category} value={category}>{MSME_LABELS[category]}</option>)}
            </select>
          </Field>
          {(msmeCategory === "micro" || msmeCategory === "small" || msmeCategory === "medium") && (
            <Field label="Udyam registration number" error={errorFor("msme")}>
              <input value={udyamNumber} onChange={(event) => setUdyamNumber(event.target.value.toUpperCase())} placeholder="UDYAM-KR-03-0001234" />
            </Field>
          )}
        </div>
        {(msmeCategory === "micro" || msmeCategory === "small") && (
          <>
            <Field label="Agreed credit period (days)" error={errorFor("msme")} hint="Leave blank to use the default 15-day limit.">
              <input type="number" min={0} max={45} value={agreedCreditDays} onChange={(event) => setAgreedCreditDays(event.target.value === "" ? "" : Number(event.target.value))} placeholder="Up to 45" />
            </Field>
            {msmePreview && (
              <p className="pf-msme-note">{msmePreview.message}</p>
            )}
          </>
        )}
      </section>

      <section className="pf-section">
        <h4>Payment terms &amp; bank</h4>
        <div className="pf-grid two">
          <Field label="Credit limit (₹)"><input type="number" min={0} value={creditLimit} onChange={(event) => setCreditLimit(event.target.value === "" ? "" : Number(event.target.value))} /></Field>
          <Field label="Credit days"><input type="number" min={0} value={creditDays} onChange={(event) => setCreditDays(event.target.value === "" ? "" : Number(event.target.value))} /></Field>
        </div>
        <div className="pf-grid two">
          <Field label="Bank account name"><input value={bankAccountName} onChange={(event) => setBankAccountName(event.target.value)} /></Field>
          <Field label="Account number"><input value={bankAccountNumber} onChange={(event) => setBankAccountNumber(event.target.value)} /></Field>
        </div>
        <div className="pf-grid two">
          <Field label="IFSC" error={errorFor("bank")}><input value={bankIfsc} onChange={(event) => setBankIfsc(event.target.value.toUpperCase())} placeholder="HDFC0001234" maxLength={11} /></Field>
          <Field label="Bank name"><input value={bankName} onChange={(event) => setBankName(event.target.value)} /></Field>
        </div>
      </section>

      {warnings.length > 0 && (
        <div className="pf-warnings">
          {warnings.map((warning, index) => <div key={index}>⚠ {warning.message}</div>)}
        </div>
      )}

      <div className="modal-actions">
        <button type="button" className="button button-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="button button-primary" disabled={saving || partnerHasErrors(issues) || !name.trim()}>
          {saving?'Saving…':seed.id ? "Save changes" : "Add partner"}
        </button>
      </div>
      </fieldset>
    </form>
  );
}

// ------------------------------------------------------- quick-create picker

export interface PartnerPickerProps {
  parties: Party[];
  value: string;
  onChange: (name: string) => void;
  roleFilter?: PartyType;
  onCreated: (party: Party) => void | Party | Promise<void | Party>;
  onSelectParty?: (party:Party) => void;
  createInitial?: Partial<Party>;
  label: string;
}

/**
 * A combobox that lets someone type a name, pick an existing match, or create
 * a new partner inline without leaving the invoice or bill they're on. This is
 * the fix for "no new vendor creation option at booking level" — the picker
 * itself decides whether the typed name exists.
 */
export function PartnerPicker({ parties, value, onChange, roleFilter, onCreated, label, onSelectParty, createInitial }: PartnerPickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const [creating, setCreating] = useState(false);
  useEffect(()=>setQuery(value),[value]);

  const pool = parties.filter(party=>party.active && (!roleFilter || party.type===roleFilter));
  const matches = query.trim()
    ? pool.filter((party) => party.name.toLowerCase().includes(query.trim().toLowerCase()))
    : pool;
  const exactMatch = pool.some((party) => party.name.toLowerCase() === query.trim().toLowerCase());

  const select = (party: Party) => {
    onChange(party.name);
    onSelectParty?.(party);
    setQuery(party.name);
    setOpen(false);
  };

  return (
    <div className="pf-picker">
      <label className="pf-field">
        <span className="pf-label">{label}</span>
        <input
          value={query}
          onChange={(event) => { setQuery(event.target.value); onChange(event.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          placeholder={`Search or type a new ${roleFilter?.toLowerCase() ?? "partner"} name`}
        />
      </label>
      {open && (
        <div className="pf-picker-menu">
          {matches.slice(0, 8).map((party) => (
            <button key={party.id} type="button" className="pf-picker-item" onMouseDown={e=>e.preventDefault()} onClick={() => select(party)}>
              <strong>{party.name}</strong>
              <span>{party.gstin ?? "No GSTIN"} · {party.state}</span>
            </button>
          ))}
          {matches.length === 0 && <div className="pf-picker-empty">No matches.</div>}
          {query.trim() && !exactMatch && (
            <button type="button" className="pf-picker-create" onMouseDown={e=>e.preventDefault()} onClick={() => { setCreating(true); setOpen(false); }}>
              + Create new {roleFilter?.toLowerCase() ?? "partner"} "{query.trim()}"
            </button>
          )}
        </div>
      )}
      {creating && <MasterDialog label={`Create ${roleFilter?.toLowerCase()??'partner'}`} onCancel={()=>setCreating(false)}>
            <h3>New {roleFilter?.toLowerCase() ?? "partner"}</h3>
            <PartnerForm initial={{ ...createInitial, name: query.trim(), type: roleFilter }}
              defaultRole={roleFilter === "Vendor" ? "vendor" : roleFilter === "Customer" ? "customer" : undefined}
              onSave={async(party) => { const saved=await onCreated(party); select(saved??party); setCreating(false); }}
              onCancel={() => setCreating(false)} />
          </MasterDialog>}
    </div>
  );
}
